import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from "react-i18next";
import { useUser } from '../../app/context/userContext';
import ChatHeader from '../common/ChatHeader';
import VectorStores from '../vectorStores/VectorStores';
import Assistants from '../assistants/Assistants';
import SecurityGroups from '../securityGroups/SecurityGroups';
import CreateAssistantForm from '../assistants/CreateAssistantForm';
import CreateVectorStoreForm from '../vectorStores/CreateVectorStoreForm';
import UserActivity from './UserActivity';
import CreateSecurityGroupForm from '../securityGroups/CreateSecurityGroupForm';
import { updateUserLanguage } from '../../app/api/apiService';

export default function AdminPanel() {
    const [activeTab, setActiveTab] = useState('activity');
    const [showForm, setShowForm] = useState(false);
    const [showDropdown, setShowDropdown] = useState(false);
    const [formType, setFormType] = useState('');
    const [sideMenuVisible, setSideMenuVisible] = useState(true);
    const navigate = useNavigate();
    const { t, i18n } = useTranslation();

    const { user, userId } = useUser();

    const handleLangChange = async (lng) => {
        i18n.changeLanguage(lng);
        if (userId) {
            try {
                await updateUserLanguage(userId, lng);
            } catch (err) {
                console.error("Failed to save user language:", err);
            }
        }
    };

    const handleTabChange = (tab) => {
        setActiveTab(tab);
        setShowDropdown(false);
        if (tab !== 'assistants') setShowForm(false);
    };

    const showCreateAssistant = () => {
        setFormType('assistant');
        setShowForm(true);
        setShowDropdown(false);
    };
    const showCreateVectorStore = () => {
        setFormType('vectorstore');
        setShowForm(true);
        setShowDropdown(false);
    };
    const showCreateSecurityGroup = () => {
        setFormType('securitygroup');
        setShowForm(true);
        setShowDropdown(false);
    };
    const goToChat = () => navigate('/');
    const handleCreateNewAssistant = () => setShowForm(false);
    const handleCreateVectorStore = () => setShowForm(false);
    const handleCreateSecurityGroup = () => setShowForm(false);

    const adminTitles = {
        activity: t("activity", "User Activity"),
        vectorstore: t("knowledge_bases", "Knowledge Bases"),
        assistants: t("assistants", "Assistants"),
        securitygroups: t("security_groups", "Security Groups"),
    };

    return (
        <div className="App" style={{ display: 'flex', flexDirection: 'row', height: '100vh', position: 'relative' }}>
            {sideMenuVisible && (
                <aside className="admin-sidebar">
                    <div
                        className="side-menu-button"
                        onMouseEnter={() => setShowDropdown(true)}
                        onMouseLeave={() => setShowDropdown(false)}
                    >
                        <span>{t("create", "Create")}</span>
                        {showDropdown && (
                            <div className="dropdown-menu">
                                <div onClick={showCreateAssistant}>{t("new_assistant", "New Assistant")}</div>
                                <div onClick={showCreateVectorStore}>{t("new_knowledge_base", "New Knowledge Base")}</div>
                                <div onClick={showCreateSecurityGroup}>{t("new_security_group", "New Security Group")}</div>
                            </div>
                        )}
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width={18}
                            height={18}
                            fill="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                fillRule="evenodd"
                                d="M12 5a1 1 0 0 1 1 1v5h5a1 1 0 1 1 0 2h-5v5a1 1 0 1 1-2 0v-5H6a1 1 0 1 1 0-2h5V6a1 1 0 0 1 1-1Z"
                                clipRule="evenodd"
                            />
                        </svg>
                    </div>
                    <ul>
                        <li className={activeTab === 'activity' ? 'active' : ''} onClick={() => handleTabChange('activity')}>
                            {t("activity", "Activity")}
                        </li>
                        <li className={activeTab === 'vectorstore' ? 'active' : ''} onClick={() => handleTabChange('vectorstore')}>
                            {t("knowledge_bases", "Knowledge Bases")}
                        </li>
                        <li className={activeTab === 'assistants' ? 'active' : ''} onClick={() => handleTabChange('assistants')}>
                            {t("assistants", "Assistants")}
                        </li>
                        <li className={activeTab === 'securitygroups' ? 'active' : ''} onClick={() => handleTabChange('securitygroups')}>
                            {t("security_groups", "Security Groups")}
                        </li>
                    </ul>
                    <div className="bottom-container">
                        <button onClick={goToChat} className="admin-button">{t("back_to_chat", "Back to chat")}</button>
                    </div>
                </aside>
            )}

                <div className={`admin-content ${sideMenuVisible ? '' : 'full-width'}`}>
                <ChatHeader
                    sideMenuVisible={sideMenuVisible}
                    toggleSideMenu={() => setSideMenuVisible(v => !v)}
                    assistantName={adminTitles[activeTab]}
                    exploring={false}
                    currentLang={i18n.language}
                    onLangChange={handleLangChange}
                />
                {activeTab === 'vectorstore' && <VectorStores />}
                {activeTab === 'assistants' && <Assistants />}
                {activeTab === 'activity' && <UserActivity />}
                {activeTab === 'securitygroups' && <SecurityGroups />}
                {showForm && formType === 'assistant' && (
                    <CreateAssistantForm
                        onSave={handleCreateNewAssistant}
                        onCancel={() => setShowForm(false)}
                    />
                )}
                {showForm && formType === 'vectorstore' && (
                    <CreateVectorStoreForm
                        onSave={handleCreateVectorStore}
                        onCancel={() => setShowForm(false)}
                    />
                )}
                {showForm && formType === 'securitygroup' && (
                    <CreateSecurityGroupForm
                        onSave={handleCreateSecurityGroup}
                        onCancel={() => setShowForm(false)}
                    />
                )}
            </div>
        </div>
    );
}